/**
 * Farbschema hell/dunkel – gilt je Benutzer (Spalte nutzer.farbschema), auf jedem Gerät und in jedem Modul.
 *  - Dunkel ist der Standard (kein Wert / NULL = dunkel).
 *  - Gerätewert in localStorage `pilot-hub-farbschema`: wird per Inline-Script in index.html VOR dem Rendern angewendet
 *    (kein Aufblitzen) und bleibt beim Abmelden stehen.
 *  - Nach dem Login gilt der Serverwert (AppShell ruft `uebernehmeServerFarbschema` mit user.farbschema auf).
 *  - Umschalten wirkt sofort (data-theme am <html>) und wird per PUT /auth/me/farbschema gespeichert.
 */
import { useSyncExternalStore } from 'react'
import { api } from '@pilot-hub/api-client'

export type Farbschema = 'hell' | 'dunkel'

export const FARBSCHEMA_KEY = 'pilot-hub-farbschema'

export function normalisiereFarbschema(v: unknown): Farbschema {
  return v === 'hell' ? 'hell' : 'dunkel'
}

function leseGeraet(): Farbschema {
  try { return normalisiereFarbschema(localStorage.getItem(FARBSCHEMA_KEY)) } catch { return 'dunkel' }
}

function anwenden(s: Farbschema) {
  if (typeof document === 'undefined') return
  const el = document.documentElement
  const t = s === 'hell' ? 'light' : 'dark'
  if (el.dataset.theme !== t) el.dataset.theme = t
}

let aktuell: Farbschema = typeof window === 'undefined' ? 'dunkel' : leseGeraet()
const hoerer = new Set<() => void>()

function setzeLokal(s: Farbschema, speichern: boolean) {
  const geaendert = aktuell !== s
  aktuell = s
  anwenden(s)
  if (speichern) { try { localStorage.setItem(FARBSCHEMA_KEY, s) } catch { /* Speicher blockiert */ } }
  if (geaendert) hoerer.forEach(h => h())
}

/** Beim Start: Gerätewert anwenden (Inline-Script in index.html hat es meist schon getan) und auf andere Tabs hören. */
export function initFarbschema(): void {
  if (typeof window === 'undefined') return
  setzeLokal(leseGeraet(), false)
  window.addEventListener('storage', e => { if (e.key === FARBSCHEMA_KEY) setzeLokal(normalisiereFarbschema(e.newValue), false) })
}

let serverUebernommenFuer: number | null = null

/**
 * Serverwert des angemeldeten Benutzers übernehmen (einmal je Benutzer und Seitenaufruf, damit ein später
 * veralteter Wert im Benutzerobjekt die Wahl des Benutzers nicht überschreibt).
 * `undefined` = API kennt das Feld nicht (alter Stand) -> Gerätewert bleibt.
 */
export function uebernehmeServerFarbschema(userId: number | undefined, wert: string | null | undefined): void {
  if (wert === undefined || userId === undefined || serverUebernommenFuer === userId) return
  serverUebernommenFuer = userId
  setzeLokal(normalisiereFarbschema(wert), true)
}

/** Benutzer wählt ein Farbschema: sofort anwenden, merken und am Server speichern (Fehler ändern die lokale Wahl nicht). */
export function waehleFarbschema(s: Farbschema): void {
  setzeLokal(s, true)
  api.put('/auth/me/farbschema', { farbschema: s }).catch(() => { /* offline / API noch ohne Endpunkt: lokale Wahl bleibt */ })
}

export function useFarbschema(): [Farbschema, (s: Farbschema) => void] {
  const s = useSyncExternalStore(
    cb => { hoerer.add(cb); return () => { hoerer.delete(cb) } },
    () => aktuell,
    () => 'dunkel' as Farbschema,
  )
  return [s, waehleFarbschema]
}

